import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, Crown, Heart, Loader2, Radio, Timer } from 'lucide-react';
import { useAuth } from '../lib/auth-context';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  BattleRoom as BattleRoomType,
  endBattle,
  getBattleRoom,
  joinBattleRoom,
  mintBattleToken,
  postBattleEvent,
  startBattle,
} from '../lib/battle-client';
import { getWalletBalance } from '../lib/wallet-client';
import { BattleGiftPanel } from '../components/BattleGiftPanel';
import { BattleSafetyControls } from '../components/BattleSafetyControls';

function formatRemaining(endsAt?: string): string {
  if (!endsAt) return '--:--';
  const remainingMs = Math.max(0, new Date(endsAt).getTime() - Date.now());
  const totalSeconds = Math.floor(remainingMs / 1000);
  const mm = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, '0');
  const ss = (totalSeconds % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}

export default function BattleRoom() {
  const { roomId = '' } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { user, accessToken } = useAuth();
  const localVideoRef = useRef<HTMLVideoElement | null>(null);

  const [room, setRoom] = useState<BattleRoomType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eventBusy, setEventBusy] = useState(false);
  const [battleTokenState, setBattleTokenState] = useState<string>('Not connected');
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [remaining, setRemaining] = useState('--:--');

  const isHost = useMemo(() => Boolean(user?.id && room?.hostUserId === user.id), [room, user?.id]);
  const opponentUserId = useMemo(() => {
    if (!room || !user?.id) return undefined;
    if (room.hostUserId === user.id) return room.opponentUserId;
    return room.hostUserId;
  }, [room, user?.id]);

  useEffect(() => {
    if (!roomId) return;
    let mounted = true;
    let pollInterval: number | undefined;

    const load = async () => {
      try {
        setLoading(true);
        const joinResult = accessToken ? await joinBattleRoom(roomId, accessToken) : await getBattleRoom(roomId, accessToken);
        if (!mounted) return;
        setRoom(joinResult.room);
      } catch (e: any) {
        if (mounted) setError(e?.message || 'Failed to load battle room');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    const poll = async () => {
      try {
        const snapshot = await getBattleRoom(roomId, accessToken);
        if (!mounted) return;
        setRoom(snapshot.room);
      } catch {
        // Quiet poll failure; next poll may recover.
      }
    };

    void load();
    pollInterval = window.setInterval(() => {
      void poll();
    }, 2500);

    return () => {
      mounted = false;
      if (pollInterval) window.clearInterval(pollInterval);
    };
  }, [roomId, accessToken]);

  useEffect(() => {
    if (!accessToken) return;
    let mounted = true;
    const run = async () => {
      try {
        const balance = await getWalletBalance(accessToken);
        if (mounted) setWalletBalance(balance.balance);
      } catch {
        // optional balance load
      }
    };
    void run();
    return () => {
      mounted = false;
    };
  }, [accessToken]);

  useEffect(() => {
    if (!room?.endsAt) {
      setRemaining('--:--');
      return;
    }
    const update = () => setRemaining(formatRemaining(room.endsAt));
    update();
    const id = window.setInterval(update, 1000);
    return () => window.clearInterval(id);
  }, [room?.endsAt]);

  useEffect(() => {
    let mounted = true;
    const setupLocalPreview = async () => {
      if (!localVideoRef.current) return;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (!mounted || !localVideoRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        localVideoRef.current.srcObject = stream;
      } catch {
        // permission denied or unavailable
      }
    };
    void setupLocalPreview();
    return () => {
      mounted = false;
      const stream = localVideoRef.current?.srcObject as MediaStream | null;
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  const connectLivekit = async () => {
    if (!accessToken || !roomId) return;
    try {
      setBattleTokenState('Connecting...');
      const token = await mintBattleToken(roomId, accessToken);
      setBattleTokenState(
        token.livekitUrl
          ? `LiveKit ready (${token.roomName})`
          : 'LiveKit token minted',
      );
    } catch (e: any) {
      setBattleTokenState(`LiveKit unavailable: ${e?.message || 'token failed'}`);
    }
  };

  const handleLike = async () => {
    if (!accessToken || !roomId || eventBusy) return;
    setEventBusy(true);
    try {
      const result = await postBattleEvent(
        roomId,
        { type: 'like', pointsDelta: 1, idempotencyKey: crypto.randomUUID() },
        accessToken,
      );
      setRoom(result.room);
    } catch (e: any) {
      setError(e?.message || 'Could not submit like');
    } finally {
      setEventBusy(false);
    }
  };

  const handleStart = async () => {
    if (!accessToken || !roomId || eventBusy) return;
    setEventBusy(true);
    try {
      const result = await startBattle(roomId, accessToken);
      setRoom(result.room);
    } catch (e: any) {
      setError(e?.message || 'Could not start battle');
    } finally {
      setEventBusy(false);
    }
  };

  const handleEnd = async () => {
    if (!accessToken || !roomId || eventBusy) return;
    setEventBusy(true);
    try {
      const result = await endBattle(roomId, accessToken);
      setRoom(result.room);
    } catch (e: any) {
      setError(e?.message || 'Could not end battle');
    } finally {
      setEventBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen tension-page-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-neutral-400" />
      </div>
    );
  }

  if (!room) {
    return (
      <div className="min-h-screen tension-page-bg px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <Button variant="ghost" onClick={() => navigate('/battle')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Battle Lobby
          </Button>
          <Card className="mt-3 p-6 text-center">
            <p className="text-neutral-600 dark:text-neutral-400">Battle room not found.</p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen tension-page-bg px-4 py-6">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button variant="ghost" onClick={() => navigate('/battle')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Battle Lobby
          </Button>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={room.status === 'active' ? 'default' : 'secondary'}>{room.status}</Badge>
            <Badge className="bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300">
              <Timer className="w-3.5 h-3.5 mr-1" />
              {remaining}
            </Badge>
            <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
              Wallet: {walletBalance}
            </Badge>
          </div>
        </div>

        <Card className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="aspect-video rounded-lg overflow-hidden bg-neutral-900/90 border border-neutral-800 relative">
                <video ref={localVideoRef} autoPlay muted playsInline className="w-full h-full object-cover opacity-85" />
                <div className="absolute top-3 left-3 flex items-center gap-2">
                  <Badge className="bg-black/50 text-white border-white/20">
                    <Radio className="w-3 h-3 mr-1 text-red-400" />
                    Live preview
                  </Badge>
                  <Badge className="bg-violet-600/90 text-white border-violet-300/30">
                    {battleTokenState}
                  </Badge>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => void connectLivekit()} disabled={!accessToken}>
                  Connect stream token
                </Button>
                {isHost && room.status !== 'active' && (
                  <Button onClick={() => void handleStart()} disabled={eventBusy || !room.opponentUserId}>
                    Start battle
                  </Button>
                )}
                {room.status === 'active' && (
                  <Button variant="destructive" onClick={() => void handleEnd()} disabled={eventBusy}>
                    End battle
                  </Button>
                )}
                <Button variant="secondary" onClick={() => void handleLike()} disabled={eventBusy || !accessToken}>
                  <Heart className="w-4 h-4 mr-2 text-pink-500" />
                  Send like (+1)
                </Button>
              </div>
            </div>

            <div className="w-full md:w-[260px] space-y-2">
              <Card className="p-3">
                <p className="text-xs text-neutral-500">Current score</p>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-neutral-600 dark:text-neutral-300">Host</span>
                    <span className="font-semibold">{room.score.host}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-neutral-600 dark:text-neutral-300">Opponent</span>
                    <span className="font-semibold">{room.score.opponent}</span>
                  </div>
                </div>
                <p className="mt-3 text-[11px] text-neutral-500">
                  Server verifies all score events and gift receipts.
                </p>
              </Card>
              <Card className="p-3">
                <p className="text-xs text-neutral-500">Winner projection</p>
                <p className="mt-2 text-sm font-medium">
                  {room.score.host === room.score.opponent
                    ? 'Tie'
                    : room.score.host > room.score.opponent
                      ? 'Host leading'
                      : 'Opponent leading'}
                </p>
                <div className="mt-2 flex items-center gap-1 text-amber-600 dark:text-amber-300 text-xs">
                  <Crown className="w-3.5 h-3.5" />
                  Live standings update every event
                </div>
              </Card>
            </div>
          </div>
        </Card>

        <BattleGiftPanel
          roomId={room.id}
          targetUserId={opponentUserId}
          accessToken={accessToken}
          disabled={room.status !== 'active'}
          onGiftSent={() => {
            void getBattleRoom(room.id, accessToken).then((snapshot) => setRoom(snapshot.room)).catch(() => {});
            void getWalletBalance(accessToken).then((balance) => setWalletBalance(balance.balance)).catch(() => {});
          }}
        />

        <BattleSafetyControls roomId={room.id} targetUserId={opponentUserId} accessToken={accessToken} />

        {error && (
          <Card className="p-3 border-red-200 dark:border-red-900 text-xs text-red-600 dark:text-red-300">
            {error}
          </Card>
        )}
      </div>
    </div>
  );
}
